import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            
            if (token && storedUser) {
                try {
                    const response = await authAPI.getMe();
                    setUser(response.data);
                } catch (error) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                }
            }
            setLoading(false);
        };

        initAuth();
    }, []);

    const login = async (email, password) => {
        const deviceToken = localStorage.getItem('device_token') || undefined;
        const response = await authAPI.login(email, password, deviceToken);
        // Se il login è completo (dispositivo attendibile) applica subito la sessione
        if (response.data.token) {
            finalizeSession(response.data);
        }
        return response.data;
    };

    const completeMfa = async (pendingToken, code, rememberDevice) => {
        const deviceToken = localStorage.getItem('device_token') || undefined;
        const response = await authAPI.mfaVerify(pendingToken, code, rememberDevice, deviceToken);
        finalizeSession(response.data);
        return response.data;
    };

    const completeMfaRecover = async (pendingToken, code) => {
        const response = await authAPI.mfaRecoverCode(pendingToken, code);
        finalizeSession(response.data);
        return response.data;
    };

    const finalizeSession = (data) => {
        localStorage.setItem('token', data.token);
        if (data.device_token) localStorage.setItem('device_token', data.device_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        // Non attivare la sessione se: deve cambiare password OPPURE ci sono codici di recupero da mostrare
        if (!data.user?.must_change_password && !data.recovery_codes) {
            setUser(data.user);
        }
    };

    const activateStoredSession = () => {
        const raw = localStorage.getItem('user');
        const u = raw ? JSON.parse(raw) : null;
        if (u && !u.must_change_password) setUser(u);
        return u;
    };

    const finishPasswordChange = async () => {
        const response = await authAPI.getMe();
        localStorage.setItem('user', JSON.stringify(response.data));
        setUser(response.data);
        return response.data;
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    const isAdmin = () => user?.role === 'admin';
    const isAreaManager = () => user?.role === 'area_manager';
    const isBackoffice = () => user?.role === 'backoffice';
    const isAddettoConsegna = () => user?.role === 'addetto_consegna';
    // Chi può vedere/filtrare i dati di più commerciali (admin, area manager o backoffice)
    const canViewTeam = () => ['admin', 'area_manager', 'backoffice'].includes(user?.role);

    return (
        <AuthContext.Provider value={{ user, loading, login, completeMfa, completeMfaRecover, activateStoredSession, finishPasswordChange, logout, isAdmin, isAreaManager, isBackoffice, isAddettoConsegna, canViewTeam }}>
            {children}
        </AuthContext.Provider>
    );
};
